import type { OptionCallPut } from '../types';
import type { FocusedElement, InstrumentType } from './types';

// Focus management - single union type for all focus behaviors
export type FocusMode = 'forward' | 'backward' | 'focus';

// Focus Actions
export type SetFocusAction = { type: 'SET_FOCUS'; element: FocusedElement };
export const setFocus = (element: FocusedElement): SetFocusAction => ({
    type: 'SET_FOCUS',
    element,
});

export type LostFocusAction = { type: 'LOST_FOCUS' };
export const lostFocus = (): LostFocusAction => ({
    type: 'LOST_FOCUS',
});

// Value Setting Actions
export type SetInstrumentTypeAction = {
    type: 'SET_INSTRUMENT_TYPE';
    value: InstrumentType;
    focus?: FocusMode;
};
export const setInstrumentType = (value: InstrumentType, focus?: FocusMode): SetInstrumentTypeAction => ({
    type: 'SET_INSTRUMENT_TYPE',
    value,
    focus,
});

export type SetUnderlyingSymbolAction = {
    type: 'SET_UNDERLYING_SYMBOL';
    value: string;
    focus?: FocusMode;
};
export const setUnderlyingSymbol = (value: string, focus?: FocusMode): SetUnderlyingSymbolAction => ({
    type: 'SET_UNDERLYING_SYMBOL',
    value,
    focus,
});

export type SetExpirationDateAction = {
    type: 'SET_EXPIRATION_DATE';
    value: string;
    focus?: FocusMode;
};
export const setExpirationDate = (value: string, focus?: FocusMode): SetExpirationDateAction => ({
    type: 'SET_EXPIRATION_DATE',
    value,
    focus,
});

export type SetOptionCallPutAction = {
    type: 'SET_OPTION_CALL_PUT';
    value: OptionCallPut | undefined;
    focus?: FocusMode;
};
export const setOptionCallPut = (value: OptionCallPut | undefined, focus?: FocusMode): SetOptionCallPutAction => ({
    type: 'SET_OPTION_CALL_PUT',
    value,
    focus,
});

export type SetStrikePriceAction = {
    type: 'SET_STRIKE_PRICE';
    value: number | undefined;
    focus?: FocusMode;
};
export const setStrikePrice = (value: number | undefined, focus?: FocusMode): SetStrikePriceAction => ({
    type: 'SET_STRIKE_PRICE',
    value,
    focus,
});

export type SetDropdownActiveIndexAction = {
    type: 'SET_DROPDOWN_ACTIVE_INDEX';
    element: FocusedElement;
    index: number;
};
export const setDropdownActiveIndex = (element: FocusedElement, index: number): SetDropdownActiveIndexAction => ({
    type: 'SET_DROPDOWN_ACTIVE_INDEX',
    element,
    index,
});

export type SetDropdownSearchTermAction = {
    type: 'SET_DROPDOWN_SEARCH_TERM';
    element: FocusedElement;
    term: string;
};
export const setDropdownSearchTerm = (element: FocusedElement, term: string): SetDropdownSearchTermAction => ({
    type: 'SET_DROPDOWN_SEARCH_TERM',
    element,
    term,
});

export type SetDropdownItemsAction = {
    type: 'SET_DROPDOWN_ITEMS';
    element: FocusedElement;
    items: any[];
    loading?: boolean;
};
export const setDropdownItems = (element: FocusedElement, items: any[], loading?: boolean): SetDropdownItemsAction => ({
    type: 'SET_DROPDOWN_ITEMS',
    element,
    items,
    loading,
});

export type NavigateDropdownAction = {
    type: 'NAVIGATE_DROPDOWN';
    element: FocusedElement;
    direction: 'up' | 'down';
};
export const navigateDropdown = (element: FocusedElement, direction: 'up' | 'down'): NavigateDropdownAction => ({
    type: 'NAVIGATE_DROPDOWN',
    element,
    direction,
});

export type ProvidedValueChangedAction = {
    type: 'PROVIDED_VALUE_CHANGED';
    payload: {
        instrumentString: string;
    };
};
export const providedValueChanged = (instrumentString: string): ProvidedValueChangedAction => ({
    type: 'PROVIDED_VALUE_CHANGED',
    payload: {
        instrumentString,
    },
});

export type OnPastedValueAction = {
    type: 'ON_PASTED_VALUE';
    payload: {
        instrumentString: string;
    };
};
export const onPastedValue = (instrumentString: string): OnPastedValueAction => ({
    type: 'ON_PASTED_VALUE',
    payload: {
        instrumentString,
    },
});

export type ResetInstrumentAction = {
    type: 'RESET_INSTRUMENT';
    payload: {
        instrumentType: InstrumentType;
        underlyingSymbol: string;
    };
};

export const resetInstrument = (instrumentType: InstrumentType, underlyingSymbol: string): ResetInstrumentAction => ({
    type: 'RESET_INSTRUMENT',
    payload: {
        instrumentType,
        underlyingSymbol,
    },
});

export type UpdateUnderlyingSymbolPricesAction = {
    type: 'UPDATE_UNDERLYING_SYMBOL_PRICE';
    bidPrice: number | undefined;
    askPrice: number | undefined;
    lastPrice: number | undefined;
};

export const updateUnderlyingSymbolPrices = (
    bidPrice: number | undefined,
    askPrice: number | undefined,
    lastPrice: number | undefined,
): UpdateUnderlyingSymbolPricesAction => ({
    type: 'UPDATE_UNDERLYING_SYMBOL_PRICE',
    bidPrice,
    askPrice,
    lastPrice,
});

// Union type of all actions
export type SymbolPickerAction =
    | SetFocusAction
    | LostFocusAction
    | SetInstrumentTypeAction
    | SetUnderlyingSymbolAction
    | SetExpirationDateAction
    | SetOptionCallPutAction
    | SetStrikePriceAction
    | SetDropdownActiveIndexAction
    | SetDropdownSearchTermAction
    | SetDropdownItemsAction
    | NavigateDropdownAction
    | ProvidedValueChangedAction
    | OnPastedValueAction
    | ResetInstrumentAction
    | UpdateUnderlyingSymbolPricesAction;
